import { Box, Center, Stack, Text } from '@mantine/core'
import type { ReactNode } from 'react'

import NextBaseImage from '@/components/basic/BaseImage'

interface MarketCardHeaderProps {
  /** EK / event-kind icon URL。通常 `event.logo_url ?? event.event_kind_group_icon`。 */
  iconSrc?: string
  /** Event-kind 展示名，如 "Pro Basketball (M)"。 */
  kindName?: string
  /** 卡片标题（赛事名）。 */
  title: ReactNode
  /** 标题下方的状态行——调用方组合（LIVE 徽标或时间文案），本组件不感知分支。 */
  subtitle: ReactNode
  /**
   * 图标呈现方式（镜像 cdc-web `MarketCardHeader`）：
   *   - `'boxed'`（默认）→ 图标放进 40×40 圆角盒（边框 + 次级底，8px 内边距
   *     → 24×24 图标）。match / futures / predict 卡用。
   *   - `'bare'` → 资产 logo 直接占满 40×40 位，无盒。recurring/crypto 卡的
   *     资产字形（BTC/双国旗）独占页头位（Figma 2466-105311）。两种变体都保
   *     留 40×40 占位，页头行高不跳。
   */
  iconVariant?: 'boxed' | 'bare'
}

const ICON_BOX_SIZE = 40
const ICON_SIZE = 24
/** bare（铺满型）资产 logo 的圆角——匹配 boxed 表面的 8px 圆角（Mantine md），
 *  方形资产字形贴边铺满时不读成尖角 chip（cdc-web 同款 BARE_ICON_RADIUS）。 */
const BARE_ICON_RADIUS = 8

/**
 * Adapted from cdc-web `Compact/_shared/MarketCardHeader.tsx`（TwoWayCard 卡片头）。
 *
 * 差异（均已决策）：
 * - cdc-market 没有 ThemeAwareAssetImage（无 light 主题，不做主题分离），
 *   改用 `NextBaseImage`（自带加载失败兜底）；`fallback={null}` → 加载失败时
 *   整个图标位留空，不留灰块；
 * - `titleLineClamp` 固定 1（Figma 单行标题），不设 prop。
 *
 * 布局：40×40 图标位（boxed = 圆角盒内 24×24 图标；bare = 40×40 直出）+ kind 名
 * （subtitle 次级色）/ heading3 标题 / subtitle 槽。
 */
export function MarketCardHeader({
  iconSrc,
  kindName,
  title,
  subtitle,
  iconVariant = 'boxed',
}: Readonly<MarketCardHeaderProps>) {
  return (
    <Stack gap="2xs" component="header" w="100%">
      {(iconSrc || kindName) && (
        <Box
          style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}
          data-testid="streamline-header-top"
        >
          {iconSrc &&
            (iconVariant === 'bare' ? (
              <Box data-testid="market-card-icon-bare" style={{ flexShrink: 0 }}>
                {/* 8px 圆角（cdc-web 同款 BARE_ICON_RADIUS）：资产 logo 铺满
                    40×40 位，圆角匹配 boxed 表面的 md 半径。之前 radius="50%"
                    圆裁是自创处理，偏离 cdc-web，已对齐。
                    objectFit contain（对齐 cdc-web ThemeAwareAssetImage 的 bare
                    渲染；NextBaseImage 默认 cover，经 style 透传覆盖）。 */}
                <NextBaseImage
                  src={iconSrc}
                  width={ICON_BOX_SIZE}
                  height={ICON_BOX_SIZE}
                  radius={BARE_ICON_RADIUS}
                  alt=""
                  unoptimized
                  fallback={null}
                  style={{ objectFit: 'contain' }}
                />
              </Box>
            ) : (
              <Center
                data-testid="market-card-icon-boxed"
                w={ICON_BOX_SIZE}
                h={ICON_BOX_SIZE}
                style={{
                  flexShrink: 0,
                  borderRadius: 'var(--mantine-radius-md)',
                  border: '1px solid var(--border-surface-default)',
                  background: 'var(--bg-surface-secondary)',
                }}
              >
                <NextBaseImage
                  src={iconSrc}
                  width={ICON_SIZE}
                  height={ICON_SIZE}
                  alt=""
                  unoptimized
                  fallback={null}
                />
              </Center>
            ))}
          {kindName && (
            <Text variant="subtitle" c="var(--content-secondary)" truncate>
              {kindName}
            </Text>
          )}
        </Box>
      )}
      <Stack gap="3xs" w="100%">
        <Text
          variant="heading3"
          c="var(--content-primary)"
          component="h2"
          lineClamp={1}
          style={{ minWidth: 0 }}
        >
          {title}
        </Text>
        {subtitle}
      </Stack>
    </Stack>
  )
}
